/**
 * Coverage-inquiry helpers — patient-mediated DIN check.
 * See docs/COVERAGE_INQUIRY_DESIGN.md for the contract.
 *
 * The bridge stores the inquiry content sealed under the per-assignment
 * symmetric key (same secretbox mechanism as the PDF/fields schema); the
 * SPA decrypts it locally and seals the patient's reply to the Mac's
 * public key. Decode + reply-payload logic lives here (pure) so it can be
 * unit-tested without the CoverageInquiryPage component.
 */

import { openSecretBox } from "./bridgeClient";

/** Outcome vocabulary — must match the design contract verbatim. */
export const COVERAGE_OUTCOMES = ["covered", "pa_required", "not_covered", "unsure"];

/**
 * Decrypt + parse the coverage payload from the bridge.
 *
 * Plaintext contract:
 *   { din, drug_name, inquiry_id,
 *     prefill: { insurer_name, member_id, plan_number, division_id },
 *     insurer_terms: { member_id_label, plan_label } }
 */
/** The insurance-details inputs collected on the coverage page. */
interface CoverageDetails {
  insurer_name?: string;
  member_id?: string;
  plan_number?: string;
  division_id?: string;
  [key: string]: unknown;
}

/** Insurer printed-terminology block carried in the coverage payload. */
interface InsurerTerms {
  member_id_label?: string;
  plan_label?: string;
  [key: string]: unknown;
}

export function decodeCoveragePayload(encryptedCoverageB64: string, symmetricKeyB64: string): any {
  const plaintext = openSecretBox(encryptedCoverageB64, symmetricKeyB64);
  return JSON.parse(new TextDecoder().decode(plaintext));
}

/**
 * Seed the insurance-details inputs from the decrypted prefill.
 * Patient-typed values always win — prefill only fills fields the patient
 * hasn't touched, so a re-fetch can never clobber what they entered.
 */
export function seedDetailsFromPrefill(typed: CoverageDetails | null | undefined, prefill: CoverageDetails | null | undefined) {
  const t = typed || {};
  const p = prefill || {};
  return {
    insurer_name: t.insurer_name || p.insurer_name || "",
    member_id: t.member_id || p.member_id || "",
    plan_number: t.plan_number || p.plan_number || "",
    division_id: t.division_id || p.division_id || "",
  };
}

/**
 * Labels for the insurance-details inputs, adapted to the insurer's printed
 * terminology (e.g. Canada Life "Plan number", Medavie "Policy number").
 * Sensible defaults when the payload carries no terms.
 */
export function insurerFieldLabels(insurerTerms: InsurerTerms | null | undefined) {
  return {
    memberIdLabel: (insurerTerms && insurerTerms.member_id_label) || "Member ID",
    planLabel: (insurerTerms && insurerTerms.plan_label) || "Group / policy number",
  };
}

/** Insurer coverage-check route carried in the coverage payload. */
interface InsurerInstructions {
  web_url?: string | null;
  web_label?: string | null;
  phone?: string | null;
  phone_hours?: string | null;
  [key: string]: unknown;
}

/**
 * The member-site link and phone line the coverage page offers as buttons.
 * Only an https link is used (anything else is dropped, so the payload can
 * never put a javascript: or http: link in front of a patient), and the
 * tel: link is built from the number's digits. Null fields mean "no button":
 * the page then points to the number on the patient's drug card.
 */
export function insurerContact(instructions: InsurerInstructions | null | undefined) {
  const i = instructions || {};
  let webUrl: string | null = null;
  let webLabel: string | null = null;
  if (typeof i.web_url === "string" && /^https:\/\//i.test(i.web_url)) {
    webUrl = i.web_url;
    webLabel = (typeof i.web_label === "string" && i.web_label.trim()) || new URL(i.web_url).host;
  }
  const digits = typeof i.phone === "string" ? i.phone.replace(/[^\d]/g, "") : "";
  const hasPhone = digits.length >= 10;
  return {
    webUrl,
    webLabel,
    phone: hasPhone ? (i.phone as string) : null,
    telHref: hasPhone ? `tel:+${digits.length === 10 ? "1" + digits : digits}` : null,
    phoneHours: hasPhone && typeof i.phone_hours === "string" && i.phone_hours ? i.phone_hours : null,
  };
}

/**
 * Build the sealed-reply plaintext (SPA → Mac) exactly per the design
 * contract. The caller seals it with sealSubmission(payload, pubkeyB64)
 * and POSTs the ciphertext via submitCoverageReply.
 */
export function buildCoverageReply({
  inquiryId,
  bridgeAssignmentId,
  outcome,
  details,
  answeredAt,
}: {
  inquiryId?: string;
  bridgeAssignmentId?: string;
  outcome?: string;
  details?: CoverageDetails;
  answeredAt?: string;
}) {
  if (!COVERAGE_OUTCOMES.includes(outcome as string)) {
    throw new Error(`buildCoverageReply: invalid outcome "${outcome}"`);
  }
  const d = details || {};
  return {
    __coverage__: true,
    inquiry_id: inquiryId || "",
    bridge_assignment_id: bridgeAssignmentId || "",
    outcome,
    insurer_name: d.insurer_name || "",
    member_id: d.member_id || "",
    plan_number: d.plan_number || "",
    division_id: d.division_id || "",
    answered_at: answeredAt || new Date().toISOString(),
  };
}
